import type { DeviceInst } from "./types";

export interface TerminalDef {
  id: string;
  label: string;
  x: number;
  y: number;
}

export interface DeviceDef {
  type: string;
  /** Arabic display name */
  name: string;
  cat: "power" | "control" | "switch" | "sensor" | "output" | "plc" | "misc";
  w: number;
  h: number;
  terminals: TerminalDef[];
  /** always-on potential seeds (active unless state.on === false) */
  sources?: { term: string; pol: "plus" | "minus" }[];
  /** internal conducting paths */
  links?: { a: string; b: string; when: (d: DeviceInst) => boolean }[];
  /** loads: energized when one side sees + and the other − */
  loads?: { a: string; b: string; key: string }[];
  /** interaction in RUN mode */
  action?: "momentary" | "toggle" | "latch" | "selector";
}

const st = (d: DeviceInst, k: string, def: boolean | number = false) =>
  d.state[k] === undefined ? def : d.state[k];
const on = (d: DeviceInst, k = "on") => Boolean(st(d, k, false));
const coil = (d: DeviceInst) => Boolean(d.state['coil']);

const T = (id: string, label: string, x: number, y: number): TerminalDef => ({
  id,
  label,
  x,
  y,
});

/* ------------------------------------------------------------------
   تحريك نقاط الكونتكتر والأوفرلود بدون تعديل الإحداثيات الأصلية
   x موجب = يمين | x سالب = يسار
   y موجب = جوه  | y سالب = فوك
   غيّر فقط الأرقام داخل الجداول أدناه.
   ------------------------------------------------------------------ */
type PointAdjust = Record<string, { x: number; y: number }>;

export const contactorPointAdjust: PointAdjust = {
  "1/L1": { x: 6, y: 6 },
  "3/L2": { x: 0, y: 6 },
  "5/L3": { x: -4, y: 5 },
  "13":   { x: 4, y: 7 },
  "21":   { x: 0, y:7 },
  "A1":   { x: -4, y: 7 },
  "14":   { x: 4, y: 7.5 },
  "22":   { x: 0, y: 6 },
  "A2":   { x: -4, y: 6 },
  "2/T1": { x: 6, y: 2 },
  "4/T2": { x: 0, y: 3 },
  "6/T3": { x: -3, y: 3},
};

export const overloadPointAdjust: PointAdjust = {
  "L1": { x: 0, y: -5 },
  "L2": { x: 0, y: -5 },
  "L3": { x: 0, y: -5 },
  "97": { x: -6, y: 0 },
  "98": { x:6, y: 0 },
  "95": { x: -6, y: 0 },
  "96": { x: 6, y: 0 },
  "T1": { x: 0, y: 6},
  "T2": { x: 0, y: 6 },
  "T3": { x: 0, y: 6},
};

const TA = (
  id: string,
  label: string,
  x: number,
  y: number,
  adjust: PointAdjust,
): TerminalDef => {
  const a = adjust[id] ?? { x: 0, y: 0 };
  return T(id, label, x + a.x, y + a.y);
};

/* ------------------------------------------------------------------ PLC */
/** CPU 1214C DC/DC/DC logical I/O. */
export const I_ADDRS = [
  ...Array.from({ length: 8 }, (_, i) => `I0.${i}`),
  ...Array.from({ length: 6 }, (_, i) => `I1.${i}`),
];
export const Q_ADDRS = [
  ...Array.from({ length: 8 }, (_, i) => `Q0.${i}`),
  ...Array.from({ length: 2 }, (_, i) => `Q1.${i}`),
];
export const AI_ADDRS = ["AI0", "AI1"];

/**
 * The user's photograph is cropped to the physical PLC body and rendered with
 * a transparent background.  These dimensions preserve that crop's exact
 * 840x829 aspect ratio while keeping the device close to the old simulator
 * height.
 */
export const PLC_W = 436;
export const PLC_H = 430;

/** Body color retained for SVG defs used by the other component drawings. */
export const PLC_BODY_COLOR = "#626A70";
export const PLC_BODY_GRADIENT = ["#7a848a", PLC_BODY_COLOR, "#4f565b"] as const;

// Real screw centres measured directly from the supplied 840x829 PLC image.
// Each clickable point uses the centre of the photographed circular screw head.
const PX = (x: number) => (x * PLC_W) / 840;
const PY = (y: number) => (y * PLC_H) / 829;
const P = (x: number, y: number) => ({ x: PX(x), y: PY(y) });

// Exact centres detected/measured on the user's actual PLC photograph.
const TOP_SCREWS = [
  P(83.5, 87.5), P(114.5, 87.5), P(146.5, 88.5), P(176.5, 88.5),
  P(210.5, 87.5), P(242.5, 87.5), P(273.5, 87.5), P(305.5, 87.5),
  P(336.5, 86.5), P(367.5, 88.5), P(398.5, 85.5), P(429.5, 87.5),
  P(460.5, 89.5), P(490.5, 86.5), P(519.5, 87.5), P(550.5, 87.5),
  P(579.5, 88.5), P(609.5, 86.5), P(658.5, 86.5), P(690.5, 86.5),
  P(721.5, 87.5),
] as const;

const BOT_SCREWS = [
  P(487.5, 770.5), P(520.5, 769.5), P(549.5, 772.5), P(581.5, 768.5),
  P(612.5, 769.5), P(643.5, 770.5), P(673.5, 770.5), P(705.5, 769.5),
  P(736.5, 769.5), P(767.5, 769.5),
] as const;

// Click area follows the photographed screw, but the visual overlay itself is
// intentionally smaller and has no permanent blue ring.
export const PLC_TERMINAL_HIT_R = PX(12.0);

// On this supplied photograph the printed "1M" and "DI a 0" markings line up
// visually with these screw centres.  The first visible DI is therefore I0.0,
// not I0.1 (the old mapping was shifted by one point in the UI).
const PHOTO_DI_SCREWS = [
  TOP_SCREWS[7], TOP_SCREWS[8], TOP_SCREWS[9], TOP_SCREWS[10],
  TOP_SCREWS[11], TOP_SCREWS[12], TOP_SCREWS[13], TOP_SCREWS[14],
  TOP_SCREWS[15], TOP_SCREWS[16], TOP_SCREWS[17],
] as const;

// The photograph exposes fewer top screw heads than the CPU's logical 14 DI
// addresses. Keep the remaining logical addresses available to Ladder/FBD and
// I/O tables, but do not invent clickable circles where the photo has none.
export const PLC_HIDDEN_TERMINALS = new Set(["I1.3", "I1.4", "I1.5", "3L+", "3M"]);

const plcTerms: TerminalDef[] = [
  // Power/reference points visible on the photograph. Unique ids are used for
  // the two L+/M pairs; their user-facing labels remain exactly L+ and M.
  T("L+", "L+", TOP_SCREWS[0].x, TOP_SCREWS[0].y),
  T("M", "M", TOP_SCREWS[1].x, TOP_SCREWS[1].y),
  T("PE", "⏚", TOP_SCREWS[2].x, TOP_SCREWS[2].y),
  T("SENSOR_L+", "L+", TOP_SCREWS[4].x, TOP_SCREWS[4].y),
  T("SENSOR_M", "M", TOP_SCREWS[5].x, TOP_SCREWS[5].y),
  T("1M", "1M", TOP_SCREWS[6].x, TOP_SCREWS[6].y),

  ...PHOTO_DI_SCREWS.map((pos, i) => T(I_ADDRS[i]!, I_ADDRS[i]!, pos.x, pos.y)),

  // Logical-only DIs that have no honest physical circle in this exact photo.
  T("I1.3", "I1.3", PX(625), PY(87)),
  T("I1.4", "I1.4", PX(635), PY(87)),
  T("I1.5", "I1.5", PX(645), PY(87)),

  T("2M", "2M", TOP_SCREWS[18].x, TOP_SCREWS[18].y),
  T("AI0", "AI0", TOP_SCREWS[19].x, TOP_SCREWS[19].y),
  T("AI1", "AI1", TOP_SCREWS[20].x, TOP_SCREWS[20].y),

  // Electrical output reference nodes are model-only in this photograph.
  T("3L+", "3L+", PX(805), PY(742)),
  T("3M", "3M", PX(817), PY(742)),
  ...Q_ADDRS.map((a, i) => T(a, a, BOT_SCREWS[i]!.x, BOT_SCREWS[i]!.y)),
];

export const DEVICES: DeviceDef[] = [
  {
    type: "plc",
    name: "PLC S7-1200 (CPU 1214C DC/DC/DC)",
    cat: "plc",
    w: PLC_W,
    h: PLC_H,
    terminals: plcTerms,
  },


  /* --------------------------------------------------------- power */
  {
    type: "psu24",
    name: "مزود طاقة 24VDC",
    cat: "power",
    w: 170,
    h: 130,
    terminals: [T("+V", "+24V", 40, 116), T("-V", "0V / M", 100, 116), T("L", "L", 140, 116)],
    sources: [
      { term: "+V", pol: "plus" },
      { term: "-V", pol: "minus" },
    ],
    action: "toggle",
  },
  {
    type: "src220",
    name: "مصدر 220VAC",
    cat: "power",
    w: 150,
    h: 110,
    terminals: [T("L", "L", 50, 96), T("N", "N", 100, 96)],
    sources: [
      { term: "L", pol: "plus" },
      { term: "N", pol: "minus" },
    ],
    action: "toggle",
  },
  {
    type: "src380",
    name: "مصدر 380VAC ثلاثي",
    cat: "power",
    w: 190,
    h: 110,
    terminals: [
      T("L1", "L1", 40, 96),
      T("L2", "L2", 80, 96),
      T("L3", "L3", 120, 96),
      T("N", "N", 160, 96),
    ],
    sources: [
      { term: "L1", pol: "plus" },
      { term: "L2", pol: "minus" },
      { term: "L3", pol: "minus" },
      { term: "N", pol: "minus" },
    ],
    action: "toggle",
  },
  {
    type: "mcb1",
    name: "قاطع MCB أحادي",
    cat: "power",
    // Exact ABB S201 image supplied by the user. Terminal centres are mapped
    // directly to the two visible screw centres in the cropped photo.
    w: 56,
    h: 260,
    terminals: [
      T("1", "1", 28.0, 43.2),
      T("2", "2", 28.0, 235.3),
    ],
    links: [{ a: "1", b: "2", when: (d) => st(d, "on", true) === true }],
    action: "toggle",
  },
  {
    // Keep the historic `mcb` type as the two-pole breaker so saved projects
    // continue to load without changing their device type.
    type: "mcb",
    name: "قاطع MCB ثنائي",
    cat: "power",
    w: 108,
    h: 240,
    terminals: [
      T("1", "1", 34.4, 49.4),
      T("3", "3", 76.2, 49.4),
      T("2", "2", 34.4, 216.9),
      T("4", "4", 76.2, 216.9),
    ],
    links: [
      { a: "1", b: "2", when: (d) => st(d, "on", true) === true },
      { a: "3", b: "4", when: (d) => st(d, "on", true) === true },
    ],
    action: "toggle",
  },
  {
    type: "mcb3",
    name: "قاطع MCB ثلاثي",
    cat: "power",
    w: 154,
    h: 260,
    // Keep the already-approved three-pole alignment unchanged.
    terminals: [
      T("1", "1", 27, 44), T("3", "3", 77, 44), T("5", "5", 127, 44),
      T("2", "2", 27, 237), T("4", "4", 77, 237), T("6", "6", 127, 237),
    ],
    links: [
      { a: "1", b: "2", when: (d) => st(d, "on", true) === true },
      { a: "3", b: "4", when: (d) => st(d, "on", true) === true },
      { a: "5", b: "6", when: (d) => st(d, "on", true) === true },
    ],
    action: "toggle",
  },

  /* ------------------------------------------------------- control */
  {
    type: "contactor",
    name: "كونتكتر Schneider TeSys LC1D25",
    cat: "control",
    // Keep the same logical 222 x 300 footprint so existing projects, labels,
    // properties and wiring behavior stay unchanged. The new LC1D25 photo is
    // centered inside that footprint without distorting its real proportions.
    w: 222,
    h: 300,
    terminals: [
      // Exact screw-centre positions measured from the user-supplied
      // LC1D25_FRONT_369.jpg, then mapped into the unchanged 222 x 300
      // contactor footprint. IDs are intentionally unchanged so old projects
      // preserve all wires and NO/NC/coil behavior.
      TA("1/L1", "1L1", 62.351, 46.847, contactorPointAdjust),
      TA("3/L2", "3L2", 110.099, 46.847, contactorPointAdjust),
      TA("5/L3", "5L3", 157.847, 47.748, contactorPointAdjust),
      TA("13", "13 NO", 92.081, 100.000, contactorPointAdjust),
      TA("21", "21 NC", 129.018, 100.000, contactorPointAdjust),
      TA("A1", "A1", 165.054, 100.000, contactorPointAdjust),
      TA("14", "14 NO", 92.081, 230.631, contactorPointAdjust),
      TA("22", "22 NC", 128.117, 230.631, contactorPointAdjust),
      TA("A2", "A2", 164.153, 230.631, contactorPointAdjust),
      TA("2/T1", "2T1", 62.70, 265.766, contactorPointAdjust),
      TA("4/T2", "4T2", 110.099, 265.766, contactorPointAdjust),
      TA("6/T3", "6T3", 156.946, 264.865, contactorPointAdjust),
    ],
    loads: [{ a: "A1", b: "A2", key: "coil" }],
    links: [
      { a: "1/L1", b: "2/T1", when: coil },
      { a: "3/L2", b: "4/T2", when: coil },
      { a: "5/L3", b: "6/T3", when: coil },
      { a: "13", b: "14", when: coil },
      { a: "21", b: "22", when: (d) => !coil(d) },
    ],
  },
  {
    type: "relay",
    name: "ريليه Schneider RXM 14 Pin",
    cat: "control",
    // نفس صورة وإعدادات ريليه محاكي الدوائر الكهربائية.
    w: 150,
    h: 190,
    terminals: [
      // مراكز الأرقام الفعلية على صورة RXM كما هي بالمحاكي الكهربائي.
      T("1", "1 NC", 120.50, 21.96),
      T("2", "2 NC", 93.00, 21.96),
      T("3", "3 NC", 65.00, 21.28),
      T("8", "8 COM", 36.25, 41.71),
      T("7", "7 COM", 63.85, 42.21),
      T("6", "6 COM", 92.75, 42.21),
      T("5", "5 COM", 121.00, 42.21),
      T("12", "12 NO", 35.00, 137.62),
      T("11", "11 NO", 62.05, 137.79),
      T("10", "10 NO", 90.75, 137.79),
      T("9", "9 NO", 118.75, 137.79),
      T("4", "4 NC", 34.50, 160.34),
      T("14", "14 A2", 62.20, 160.11),
      T("13", "13 A1", 89.45, 160.25),
    ],
    loads: [{ a: "13", b: "14", key: "coil" }],
    links: [
      // 5 COM → 1 NC / 9 NO
      { a: "5", b: "1", when: (d) => !coil(d) },
      { a: "5", b: "9", when: coil },
      // 6 COM → 2 NC / 10 NO
      { a: "6", b: "2", when: (d) => !coil(d) },
      { a: "6", b: "10", when: coil },
      // 7 COM → 3 NC / 11 NO
      { a: "7", b: "3", when: (d) => !coil(d) },
      { a: "7", b: "11", when: coil },
      // 8 COM → 4 NC / 12 NO
      { a: "8", b: "4", when: (d) => !coil(d) },
      { a: "8", b: "12", when: coil },
    ],
  },
  {
    type: "timer",
    name: "تايمر",
    cat: "control",
    // نفس PF083A الموجود في محاكي الدوائر الكهربائية.
    w: 155,
    h: 170,
    terminals: [
      // فوق: 6 - 5 - 4 - 3
      T("6", "6 NC", 35.03, 29.02),
      T("5", "5 NO", 62.83, 29.02),
      T("4", "4 NO", 91.73, 29.02),
      T("3", "3 NC", 119.64, 29.02),
      // جوه: 7 - 8 - 1 - 2
      T("7", "7 A2", 36.03, 137.59),
      T("8", "8 COM", 63.33, 137.59),
      T("1", "1 COM", 91.23, 137.59),
      T("2", "2 A1", 119.64, 137.59),
    ],
    // الملف 2/7. محاكي PLC ثنائي الجهد، لذلك يشتغل مع مصدر 24VDC أو 220VAC.
    loads: [{ a: "2", b: "7", key: "coil" }],
    links: [
      // قبل انتهاء الزمن: NC مغلقة، NO مفتوحة.
      { a: "1", b: "3", when: (d) => Number(st(d, "t", 0)) < Number(d.props["preset"] ?? 3) },
      { a: "8", b: "6", when: (d) => Number(st(d, "t", 0)) < Number(d.props["preset"] ?? 3) },
      // بعد انتهاء الزمن: NO تغلق.
      { a: "1", b: "4", when: (d) => Number(st(d, "t", 0)) >= Number(d.props["preset"] ?? 3) },
      { a: "8", b: "5", when: (d) => Number(st(d, "t", 0)) >= Number(d.props["preset"] ?? 3) },
    ],
  },
  {
    type: "overload",
    name: "Overload Relay",
    cat: "control",

    // نفس Schneider TeSys LRD06 الموجود في محاكي الدوائر الكهربائية.
    w: 180,
    h: 250,

    // نفس معرفات الأطراف ونفس مراكز الدوائر/البراغي الفعلية.
    terminals: [
      TA("L1", "L1", 54.952, 30.271, overloadPointAdjust),
      TA("L2", "L2", 94.878, 30.354, overloadPointAdjust),
      TA("L3", "L3", 133.824, 29.459, overloadPointAdjust),
      TA("97", "97 NO", 48.552, 156.422, overloadPointAdjust),
      TA("98", "98 NO", 71.061, 156.408, overloadPointAdjust),
      TA("95", "95 NC", 118.085, 156.425, overloadPointAdjust),
      TA("96", "96 NC", 141.352, 156.391, overloadPointAdjust),
      TA("T1", "T1", 52.513, 193.845, overloadPointAdjust),
      TA("T2", "T2", 94.907, 193.831, overloadPointAdjust),
      TA("T3", "T3", 137.620, 193.820, overloadPointAdjust),
    ],

    // نفس عمل الأوفرلود في محاكي الدوائر الكهربائية.
    links: [
      { a: "L1", b: "T1", when: (d) => !on(d, "tripped") },
      { a: "L2", b: "T2", when: (d) => !on(d, "tripped") },
      { a: "L3", b: "T3", when: (d) => !on(d, "tripped") },
      { a: "95", b: "96", when: (d) => !on(d, "tripped") },
      { a: "97", b: "98", when: (d) => on(d, "tripped") },
    ],
  },

  /* -------------------------------------------------------- switches */
  {
    type: "pb_start",
    name: "زر تشغيل (NO)",
    cat: "switch",
    w: 110,
    h: 110,
    terminals: [T("13", "13", 30, 96), T("14", "14", 80, 96)],
    links: [{ a: "13", b: "14", when: (d) => on(d, "pressed") }],
    action: "momentary",
  },
  {
    type: "pb_stop",
    name: "زر إيقاف (NC)",
    cat: "switch",
    w: 110,
    h: 110,
    terminals: [T("11", "11", 30, 96), T("12", "12", 80, 96)],
    links: [{ a: "11", b: "12", when: (d) => !on(d, "pressed") }],
    action: "momentary",
  },
  {
    type: "estop",
    name: "إيقاف طارئ",
    cat: "switch",
    w: 120,
    h: 120,
    terminals: [T("11", "11", 35, 106), T("12", "12", 85, 106)],
    links: [{ a: "11", b: "12", when: (d) => !on(d, "latched") }],
    action: "latch",
  },
  {
    type: "selector",
    name: "سيلكتور AUTO/MAN",
    cat: "switch",
    w: 110,
    h: 120,
    terminals: [T("COM", "COM", 25, 106), T("A", "AUTO", 60, 106), T("M", "MAN", 95, 106)],
    links: [
      { a: "COM", b: "A", when: (d) => !on(d, "man") },
      { a: "COM", b: "M", when: (d) => on(d, "man") },
    ],
    action: "selector",
  },
  {
    type: "limit",
    name: "لمت سويج",
    cat: "sensor",
    w: 110,
    h: 140,
    terminals: [T("COM", "COM", 25, 126), T("NO", "NO", 60, 126), T("NC", "NC", 95, 126)],
    links: [
      { a: "COM", b: "NO", when: (d) => on(d) },
      { a: "COM", b: "NC", when: (d) => !on(d) },
    ],
    action: "toggle",
  },
  {
    type: "prox",
    name: "بروكسميتي",
    cat: "sensor",
    w: 150,
    h: 90,
    terminals: [T("BN", "BN +", 20, 78), T("BU", "BU −", 55, 78), T("BK", "BK out", 90, 78)],
    links: [{ a: "BN", b: "BK", when: (d) => on(d) }],
    action: "toggle",
  },

  /* --------------------------------------------------------- outputs */
  {
    type: "lamp",
    name: "لمبة بيان",
    cat: "output",
    w: 90,
    h: 110,
    terminals: [T("X1", "X1", 25, 96), T("X2", "X2", 65, 96)],
    loads: [{ a: "X1", b: "X2", key: "lit" }],
  },
  {
    type: "buzzer",
    name: "جرس إنذار",
    cat: "output",
    w: 100,
    h: 110,
    terminals: [T("+", "+", 30, 96), T("-", "−", 70, 96)],
    loads: [{ a: "+", b: "-", key: "lit" }],
  },
  {
    type: "motor",
    name: "ماطور ثلاثي",
    cat: "output",
    w: 200,
    h: 160,
    terminals: [T("U", "U", 45, 14), T("V", "V", 85, 14), T("W", "W", 125, 14)],
    // Three-phase motor state (RUN + FORWARD/REVERSE) is resolved from the
    // actual L1/L2/L3 phase order in electrical.ts.  Keeping it out of the
    // generic +/- load solver lets swapping any two phases reverse direction.
  },
  {
    type: "motor_star_delta",
    name: "ماطور ستار دلتا (6 أطراف)",
    cat: "output",
    w: 240,
    h: 210,
    // Six real motor-lead terminals.  The simulator detects STAR when
    // U2/V2/W2 are tied together, and DELTA when the standard cross-links are
    // wired: U1-W2, V1-U2, W1-V2.
    terminals: [
      T("U1", "U1", 58, 24),
      T("V1", "V1", 95, 24),
      T("W1", "W1", 132, 24),
      T("U2", "U2", 58, 54),
      T("V2", "V2", 95, 54),
      T("W2", "W2", 132, 54),
    ],
  },
  {
    type: "hmi",
    name: "شاشة HMI",
    cat: "misc",
    w: 220,
    h: 160,
    terminals: [T("L+", "L+", 30, 150), T("M", "M", 70, 150)],
  },
  {
    type: "terminals",
    name: "ترمنلات",
    cat: "misc",
    w: 240,
    h: 80,
    terminals: Array.from({ length: 8 }, (_, i) => T(`t${i}`, `${i + 1}`, 20 + i * 28, 14)).concat(
      Array.from({ length: 8 }, (_, i) => T(`b${i}`, `${i + 1}`, 20 + i * 28, 66)),
    ),
    links: Array.from({ length: 8 }, (_, i) => ({
      a: `t${i}`,
      b: `b${i}`,
      when: () => true,
    })),
  },
];

export const DEF: Record<string, DeviceDef> = Object.fromEntries(
  DEVICES.map((d) => [d.type, d]),
);

export function terminalPos(d: DeviceInst, termId: string) {
  const def = DEF[d.type];
  const t = def?.terminals.find((x) => x.id === termId);
  if (!t) return null;
  return { x: d.x + t.x, y: d.y + t.y };
}

export const CATEGORIES: { id: DeviceDef["cat"]; label: string }[] = [
  { id: "plc", label: "PLC" },
  { id: "power", label: "مصادر وحماية" },
  { id: "control", label: "تحكم" },
  { id: "switch", label: "مفاتيح وأزرار" },
  { id: "sensor", label: "حساسات" },
  { id: "output", label: "مخرجات" },
  { id: "misc", label: "أخرى" },
];
